export interface RealTimeMetrics {
  totalRevenue: number
  totalTransactions: number
  activeUsers: number
  successRate: number
  averageAmount: number
  topProvider: string
  dailyGrowth: number
  monthlyGrowth: number
}

export interface TransactionData {
  id: string
  amount: number
  currency: string
  provider: string
  phone: string
  status: 'completed' | 'pending' | 'failed'
  description: string
  created_at: string
  completed_at?: string
  country: string
  payment_method: 'standard' | 'qr_code'
}

export class RealDataService {
  private static instance: RealDataService

  public static getInstance(): RealDataService {
    if (!RealDataService.instance) {
      RealDataService.instance = new RealDataService()
    }
    return RealDataService.instance
  }

  // Retourner des métriques vides pour un nouveau compte
  async getRealTimeMetrics(): Promise<RealTimeMetrics> {
    return {
      totalRevenue: 0,
      totalTransactions: 0,
      activeUsers: 0,
      successRate: 0,
      averageAmount: 0,
      topProvider: '',
      dailyGrowth: 0,
      monthlyGrowth: 0
    }
  }

  // Retourner une liste vide pour un nouveau compte
  async getTransactions(limit: number = 20): Promise<TransactionData[]> {
    return []
  }

  // Retourner des analytics vides pour un nouveau compte
  async getAnalyticsData() {
    // Données vides pour les graphiques
    const emptyDailyData = Array.from({ length: 7 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - (6 - i))
      return {
        name: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
        transactions: 0,
        revenue: 0
      }
    })
    
    return {
      dailyData: emptyDailyData,
      providerData: [],
      totalTransactions: 0,
      totalRevenue: 0
    }
  }

  // Simuler des données temps réel vides
  async getRealtimeUpdates(): Promise<{ newTransactions: TransactionData[]; metrics: RealTimeMetrics }> {
    const metrics = await this.getRealTimeMetrics()
    return { newTransactions: [], metrics }
  }

  // Exporter des données réelles (vides au début)
  async exportData(format: 'csv' | 'json' | 'pdf' = 'csv'): Promise<string> {
    const transactions = await this.getTransactions(100)
    const metrics = await this.getRealTimeMetrics()
    
    if (format === 'json') {
      return JSON.stringify({
        export_date: new Date().toISOString(),
        metrics,
        transactions,
        summary: {
          total_transactions: 0,
          completed_transactions: 0,
          total_revenue: 0
        }
      }, null, 2)
    }
    
    if (format === 'csv') {
      const headers = ['ID', 'Montant', 'Devise', 'Provider', 'Téléphone', 'Statut', 'Pays', 'Type', 'Description', 'Date Création', 'Date Completion']
      return [headers].map(row => row.join(',')).join('\n')
    }
    
    return `PayFuse Export Report
Generated: ${new Date().toLocaleString()}

METRICS:
- Total Revenue: 0 XOF
- Total Transactions: 0
- Success Rate: 0%
- Active Users: 0

No transactions found. Start by creating your first payment!
`
  }

  // Données pour les tests de performance (vides)
  async getPerformanceMetrics() {
    return {
      apiResponseTime: 0,
      databaseLatency: 0,
      webhookDeliveryTime: 0,
      uptime: 100,
      requestsPerMinute: 0,
      errorRate: 0,
      activeConnections: 0,
      memoryUsage: 0,
      cpuUsage: 0
    }
  }
}

export default RealDataService